import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { DialogContent, MenuItem, TextField, Grid, Button } from '@mui/material';
import Dialog from 'src/components/Dialog/Dialog';
import { loadParameters } from 'src/store/actions/parameterActions';
import { selectParameterList } from 'src/store/selectors/parameterSelector';
import { ParameterSection } from 'src/utils/constants/parameters';

// MUI's Select renders blank (not the matching MenuItem's label) for a
// controlled value of '' unless displayEmpty is set — an explicit sentinel
// sidesteps that entirely instead of special-casing the empty string.
const NONE_VALUE = 'NONE';

const AssignFideicomisoDialog = ({ open, contract, handleClose, onSubmit }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const parameters = useSelector(selectParameterList);

  useEffect(() => {
    if (open) {
      dispatch(loadParameters({ section: ParameterSection.FIDEICOMISOS }));
    }
  }, [open, dispatch]);

  // The submitted value is the parameter's name (a stable reference), not
  // its current value (display text) — so renaming a fideicomiso later is
  // reflected everywhere it's assigned instead of freezing the old text.
  const fideicomisoOptions = useMemo(
    () =>
      parameters
        .filter(parameter => parameter.section === ParameterSection.FIDEICOMISOS)
        .map(parameter => ({ key: parameter.name, label: parameter.value })),
    [parameters]
  );

  const initialValues = useMemo(
    () => ({ fideicomiso: contract.fideicomiso ?? NONE_VALUE }),
    [contract.fideicomiso]
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      disableEscapeKeyDown
      title={t('CONTRACT_VIEW.CONTRACT_DETAIL.ASSIGN_FIDEICOMISO')}
    >
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={values =>
            onSubmit({
              fideicomiso:
                values.fideicomiso === NONE_VALUE ? null : values.fideicomiso
            })
          }
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <TextField
                select
                fullWidth
                margin="normal"
                name="fideicomiso"
                label={t('CONTRACT_VIEW.FIDEICOMISO')}
                value={values.fideicomiso}
                onChange={handleChange}
                variant="outlined"
              >
                <MenuItem value={NONE_VALUE}>{t('COMMON.NONE')}</MenuItem>
                {fideicomisoOptions.map(option => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Grid container justifyContent="flex-end" marginTop={2}>
                <Grid item>
                  <Button
                    type="submit"
                    sx={{
                      height: '40px',
                      padding: '0 20px',
                      fontSize: '14px',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderRadius: '4px',
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                  >
                    {t('COMMON.SAVE')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AssignFideicomisoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  contract: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default AssignFideicomisoDialog;
